import {useAuthStore} from "@/stores/authStore"
import {formatDuration} from "@/composables/duration"
import {useRouter} from 'vue-router'

// Define the navigateTo composable
export function useNavigation() {
    const router = useRouter()

    // Function to navigate to a specific route
    function navigateTo(routeName, params = {}) {
        if (params && Object.keys(params).length) {
            router.push({name: routeName, params});
        } else if (router?.push) {
            router.push({name: routeName});
        }
    }

    return {navigateTo}
}

export function strOperations() {
    // Function to Capitalize string
    function capitalize(string) {
        if (string) {
            return string.split(' ').map((str) => {
                return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
            }).join(' ')
        }
    }

    function formatEnumString(string) {
        return string
            .toLowerCase() // Convert to lowercase: "motion_in_progress"
            .replace(/_/g, ' ')   // Replace underscores with spaces: "motion in progress"
            .replace(/\b\w/g, c => c.toUpperCase()); // Capitalize first letter of each word: "Motion In Progress"
    }

    function formatVoltage(voltage) {
        voltage = parseFloat(voltage);
        return (isNaN(voltage)) ? '0v' : `${(voltage / 1000).toFixed(2)}v`;
    }

    function unitifyDistance(speed, type = 'speed', baseUnit = 'miles') {
        const authStore = useAuthStore();
        const userPrefersKm = authStore.configs?.distance_unit === 'km';

        // Absence passes straight through, but a genuine 0 must not: `!speed`
        // treated "0 km travelled" as "no value" and rendered a bare 0 with no
        // unit beside properly formatted neighbours.
        if (speed === null || speed === undefined || speed === '') return speed;

        const parsedSpeed = parseFloat(speed);
        if (Number.isNaN(parsedSpeed)) return speed;
        let convertedValue = parsedSpeed;
        let unitLabel = '';

        // Conversion logic
        if (userPrefersKm && baseUnit === 'miles') {
            convertedValue = parsedSpeed * 1.609;
        } else if (!userPrefersKm && baseUnit === 'km') {
            convertedValue = parsedSpeed / 1.609;
        }

        // Unit label logic
        if (type === 'speed') {
            unitLabel = userPrefersKm ? 'kph' : 'mph';
        } else if (type === 'distance') {
            unitLabel = userPrefersKm ? 'km' : 'mi';
        } else {
            unitLabel = type; // fallback for custom types
        }

        return `${convertedValue.toFixed(2)}${unitLabel}`;
    }


    /**
     * Resolve a speed in MPH.
     *
     * gps_speed is NOT one scale. Orion hardware writes plain MPH; the phone app
     * writes km/h x2. Only the server knows which device wrote a row, so it now
     * sends a resolved speed_mph alongside the raw field, on the REST resources
     * and on the gateway's MQTT payload.
     *
     * Accepts either the whole object (preferred - it can then use speed_mph) or
     * a bare number, which is treated as the phone's km/h x2 exactly as before.
     * That fallback matters: a Go account can hold a hardware tracker as well as
     * a phone, and until every layer is deployed the old reading has to keep
     * rendering unchanged rather than becoming a different wrong number.
     *
     * Returns null when there is no reading at all, so "no fix" stays distinct
     * from "stopped".
     */
    function speedToMph(source) {
        const isObject = source !== null && typeof source === 'object';
        if (isObject) {
            const mph = source.speed_mph;
            if (mph !== undefined && mph !== null) {
                const parsed = parseFloat(mph);
                return Number.isNaN(parsed) ? null : parsed;
            }
        }

        const raw = isObject ? source.gps_speed : source;
        if (raw === null || raw === undefined || raw === '') return null;
        const v = parseFloat(raw);
        if (Number.isNaN(v)) return null;

        // The historical reading: halve to km/h, then to MPH.
        return v / 2 * 0.621371;
    }

    function unitifySpeed(source) {
        const mph = speedToMph(source);
        if (mph === null) return null;
        return unitifyDistance(mph);
    }

    return {
        capitalize, formatEnumString, formatVoltage, unitifyDistance, speedToMph, unitifySpeed, formatDuration
    }
}
